import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

/**
 * Generate a PDF report of one or more recipes.
 * Each recipe shows ingredients table, procedure steps, assembly and serving instructions.
 * Opens in a new browser tab.
 *
 * @param {Object} params
 * @param {Array} params.recipes - Recipe objects with ingredients, steps, how_to_assemble, how_to_serve
 * @param {Array} params.ingredients - All ingredients (for name lookup by ID)
 */
export function generateRecipePdfReport({ recipes, ingredients }) {
  if (!recipes?.length) return { error: "No recipes to export." };

  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 14;

  // Build ingredient lookup map: id → name
  const ingMap = {};
  (ingredients || []).forEach((i) => {
    ingMap[i.id] = i.name;
  });

  recipes.forEach((recipe, recipeIdx) => {
    // Page break between recipes (not before the first)
    if (recipeIdx > 0) doc.addPage();

    // ── Header ──────────────────────────────────────────────────────────────
    let y = margin;
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.text("Pizza Felice LLC", margin, y);
    doc.text("Recipes", pageWidth / 2, y, { align: "center" });
    doc.setFont("helvetica", "normal");
    doc.text("Internal", pageWidth - margin, y, { align: "right" });
    y += 8;

    // ── Recipe name + cost ──────────────────────────────────────────────────
    doc.setFontSize(13);
    doc.setFont("helvetica", "bold");
    doc.text(recipe.name || "Untitled Recipe", margin, y);
    // Cost and yield info on the right side
    const rightParts = [];
    if (recipe.yield_kg != null) rightParts.push(`Yield: ${Number(recipe.yield_kg)} kg`);
    if (recipe.cost !== undefined && recipe.cost !== null) rightParts.push(`Cost: ${Number(recipe.cost).toFixed(2)} AED`);
    if (rightParts.length) {
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.text(rightParts.join("  |  "), pageWidth - margin, y, { align: "right" });
    }
    y += 6;

    // Last updated info
    if (recipe.updated_at || recipe.updated_by) {
      const updParts = [];
      if (recipe.updated_at) updParts.push(`Updated: ${new Date(recipe.updated_at).toLocaleDateString("en-GB")}`);
      if (recipe.created_at && !recipe.updated_at) updParts.push(`Created: ${new Date(recipe.created_at).toLocaleDateString("en-GB")}`);
      if (recipe.updated_by) updParts.push(`by ${recipe.updated_by}`);
      if (updParts.length) {
        doc.setFontSize(8);
        doc.setFont("helvetica", "italic");
        doc.setTextColor(120);
        doc.text(updParts.join("  "), margin, y);
        doc.setTextColor(0);
        doc.setFont("helvetica", "normal");
        y += 5;
      }
    }

    // ── Notes (if present) ───────────────────────────────────────────────────
    if (recipe.notes) {
      doc.setFontSize(9);
      doc.setFont("helvetica", "italic");
      const noteLines = doc.splitTextToSize(recipe.notes, pageWidth - margin * 2);
      const noteHeight = noteLines.length * 4.5;
      y = checkPageBreak(doc, y, noteHeight, pageHeight, margin);
      doc.text(noteLines, margin, y);
      y += noteHeight + 3;
      doc.setFont("helvetica", "normal");
    }

    // ── Ingredients table (direct + sub-recipe ingredients) ─────────────────
    const recipeIngredients = recipe.ingredients || [];
    const subRecipes = recipe.sub_recipes || [];
    const hasIngredients = recipeIngredients.length || subRecipes.length;

    if (hasIngredients) {
      // Build table rows — direct ingredients first
      const body = recipeIngredients.map((line) => {
        const name = ingMap[line.ingredient_id] || `ID ${line.ingredient_id}`;
        const qty = Number(line.quantity || 0);
        const cost = lineCost(line, ingredients);
        return [name, fmtQty(qty), cost !== null ? fmtAmt(cost) : "-"];
      });

      // Append sub-recipe sections: header row + ingredient rows
      let totalCost = recipeIngredients.reduce((sum, line) => {
        const c = lineCost(line, ingredients);
        return sum + (c || 0);
      }, 0);

      for (const sub of subRecipes) {
        const multiplier = Number(sub.multiplier || 1);
        const yieldKg = sub.sub_recipe_yield_kg;
        // When yield_kg is set, quantity is in grams — compute batch fraction
        const factor = yieldKg != null
          ? multiplier / (Number(yieldKg) * 1000)
          : multiplier;
        // Build sub-recipe display name — include size and yield when available
        let subName = sub.sub_recipe_name || "Sub-recipe";
        if (sub.sub_recipe_size) subName += ` ${sub.sub_recipe_size}`;
        if (yieldKg != null) subName += ` ${fmtQty(Number(yieldKg) * 1000)} gm`;
        const subIngs = sub.ingredients || [];
        const subIngCost = subIngs.reduce((s, line) => s + Number(line.line_cost || 0), 0);
        const subTotal = subIngCost * factor;
        totalCost += subTotal;

        // Sub-recipe header row — qty column shows grams or multiplier
        const qtyCell = yieldKg != null ? fmtQty(multiplier) : `×${multiplier}`;
        body.push([
          { content: subName, styles: { fontStyle: "bold", fillColor: [245, 245, 245] } },
          { content: qtyCell, styles: { halign: "right", fontStyle: "bold", fillColor: [245, 245, 245] } },
          { content: fmtAmt(subTotal), styles: { halign: "right", fontStyle: "bold", fillColor: [245, 245, 245] } },
        ]);
      }

      autoTable(doc, {
        startY: y,
        margin: { left: margin, right: margin },
        head: [["Ingredient", "Qty (g)", "Cost"]],
        body,
        foot: [[
          "",
          { content: "Total", styles: { halign: "right" } },
          { content: fmtAmt(totalCost), styles: { halign: "right" } },
        ]],
        theme: "grid",
        styles: { fontSize: 8, cellPadding: 2 },
        headStyles: {
          fillColor: [97, 97, 97],
          textColor: [255, 255, 255],
          fontStyle: "bold",
          fontSize: 8,
        },
        footStyles: {
          fillColor: [240, 240, 240],
          textColor: [0, 0, 0],
          fontStyle: "bold",
          fontSize: 8,
        },
        columnStyles: {
          0: { cellWidth: "auto" },
          1: { cellWidth: 28, halign: "right" },
          2: { cellWidth: 28, halign: "right" },
        },
      });

      // Update y position after table
      y = doc.lastAutoTable.finalY + 6;
    }

    // ── Procedure steps ─────────────────────────────────────────────────────
    const steps = (recipe.steps || []).map((s) => (typeof s === "object" ? s.step : s)).filter(Boolean);
    if (steps.length) {
      y = checkPageBreak(doc, y, 20, pageHeight, margin);
      doc.setFontSize(10);
      doc.setFont("helvetica", "bold");
      doc.text("Procedure", margin, y);
      y += 5;

      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      steps.forEach((step, idx) => {
        // Wrap long step text within page margins
        const stepText = `${idx + 1}. ${step}`;
        const lines = doc.splitTextToSize(stepText, pageWidth - margin * 2);
        const blockHeight = lines.length * 4.5;
        y = checkPageBreak(doc, y, blockHeight, pageHeight, margin);
        doc.text(lines, margin, y);
        y += blockHeight + 2;
      });
      y += 2;
    }

    // ── How to Assemble ─────────────────────────────────────────────────────
    if (recipe.how_to_assemble) {
      y = renderTextSection(doc, "How to Assemble", recipe.how_to_assemble, y, margin, pageWidth, pageHeight);
    }

    // ── How to Serve ────────────────────────────────────────────────────────
    if (recipe.how_to_serve) {
      y = renderTextSection(doc, "How to Serve", recipe.how_to_serve, y, margin, pageWidth, pageHeight);
    }
  });

  // ── Footer on all pages — timestamp + page numbers ──────────────────────
  const totalPages = doc.internal.getNumberOfPages();
  const now = new Date();
  const dateStr = now.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  const timeStr = now.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });

  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(130, 130, 130);
    doc.setFont("helvetica", "normal");
    doc.text(`Generated on ${dateStr} at ${timeStr}`, margin, pageHeight - 6);
    doc.text(`Page ${i} / ${totalPages}`, pageWidth - margin, pageHeight - 6, { align: "right" });
  }

  // Open in new tab
  doc.setTextColor(0, 0, 0);
  const blob = doc.output("blob");
  const url = URL.createObjectURL(blob);
  window.open(url, "_blank");

  return { success: true };
}

/**
 * Render a labeled text section with basic markdown support.
 * Supports: **bold**, *italic*, bullet lists (- or *), sub-headers (### or ##).
 * Returns updated y position.
 */
function renderTextSection(doc, label, text, y, margin, pageWidth, pageHeight) {
  y = checkPageBreak(doc, y, 16, pageHeight, margin);
  doc.setFontSize(10);
  doc.setFont("helvetica", "bold");
  doc.text(label, margin, y);
  y += 5;

  const maxWidth = pageWidth - margin * 2;
  const lineHeight = 4.5;
  const rawLines = text.split("\n");

  for (const rawLine of rawLines) {
    const trimmed = rawLine.trim();
    if (!trimmed) { y += 2; continue; } // blank line → small gap

    // Sub-header (### or ##)
    if (/^#{2,3}\s+/.test(trimmed)) {
      const headerText = trimmed.replace(/^#{2,3}\s+/, "");
      y = checkPageBreak(doc, y, 10, pageHeight, margin);
      doc.setFontSize(9);
      doc.setFont("helvetica", "bold");
      doc.text(headerText, margin, y);
      y += lineHeight + 1;
      continue;
    }

    // Bullet list item (- or * at start)
    const bulletMatch = trimmed.match(/^[-*]\s+(.*)/);
    const isBullet = !!bulletMatch;
    const lineText = isBullet ? bulletMatch[1] : trimmed;
    const indent = isBullet ? 4 : 0; // indent bullet content
    const bulletX = margin + indent;
    const contentWidth = maxWidth - indent;

    // Render inline markdown spans (bold/italic) within the line
    y = renderMarkdownLine(doc, lineText, bulletX, y, contentWidth, lineHeight, pageHeight, margin, isBullet);
  }

  y += 4;
  return y;
}

/**
 * Render a single line with inline **bold** and *italic* markdown spans.
 * Draws a bullet dot if isBullet is true.
 * Returns updated y position.
 */
function renderMarkdownLine(doc, text, x, y, maxWidth, lineHeight, pageHeight, margin, isBullet) {
  // Parse inline spans: **bold**, *italic*, plain text
  const spans = [];
  const re = /(\*\*(.+?)\*\*|\*(.+?)\*)/g;
  let lastIdx = 0;
  let match;
  while ((match = re.exec(text)) !== null) {
    if (match.index > lastIdx) spans.push({ text: text.slice(lastIdx, match.index), style: "normal" });
    if (match[2]) spans.push({ text: match[2], style: "bold" });
    else if (match[3]) spans.push({ text: match[3], style: "italic" });
    lastIdx = re.lastIndex;
  }
  if (lastIdx < text.length) spans.push({ text: text.slice(lastIdx), style: "normal" });

  // If no markdown spans, use simple text rendering (handles word wrap)
  if (spans.length === 1 && spans[0].style === "normal") {
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    const wrapped = doc.splitTextToSize(text, maxWidth);
    const blockH = wrapped.length * lineHeight;
    y = checkPageBreak(doc, y, blockH, pageHeight, margin);
    if (isBullet) { doc.text("•", x - 3, y); }
    doc.text(wrapped, x, y);
    y += blockH + 1;
    return y;
  }

  // Mixed spans — render sequentially on one line
  // First calculate total width to check if it fits, fall back to simple render if too wide
  doc.setFontSize(9);
  let totalW = 0;
  for (const s of spans) {
    doc.setFont("helvetica", s.style);
    totalW += doc.getTextWidth(s.text);
  }
  if (totalW > maxWidth) {
    // Too wide — fall back to plain text wrap (strip markdown)
    const plain = text.replace(/\*\*(.+?)\*\*/g, "$1").replace(/\*(.+?)\*/g, "$1");
    doc.setFont("helvetica", "normal");
    const wrapped = doc.splitTextToSize(plain, maxWidth);
    const blockH = wrapped.length * lineHeight;
    y = checkPageBreak(doc, y, blockH, pageHeight, margin);
    if (isBullet) { doc.text("•", x - 3, y); }
    doc.text(wrapped, x, y);
    y += blockH + 1;
    return y;
  }

  y = checkPageBreak(doc, y, lineHeight, pageHeight, margin);
  if (isBullet) { doc.setFont("helvetica", "normal"); doc.text("•", x - 3, y); }
  let cx = x;
  for (const s of spans) {
    doc.setFont("helvetica", s.style);
    doc.text(s.text, cx, y);
    cx += doc.getTextWidth(s.text);
  }
  y += lineHeight + 1;
  return y;
}

/**
 * Check if there's enough space on the current page; if not, add a new page.
 * Returns the y position to use (reset to margin on new page).
 */
function checkPageBreak(doc, y, requiredHeight, pageHeight, margin) {
  if (y + requiredHeight > pageHeight - 14) {
    doc.addPage();
    return margin;
  }
  return y;
}

/** Calculate cost for a single ingredient line (quantity in grams, price per kg) */
function lineCost(line, ingredients) {
  if (!line.ingredient_id || !line.quantity) return null;
  const ing = (ingredients || []).find((i) => i.id === line.ingredient_id);
  if (!ing) return null;
  // Apply wastage factor: effective cost = price / (1 - wastage%)
  const wastage = Number(ing.wastage || 0);
  const effectivePrice = Number(ing.price_per_unit) / (1 - wastage / 100);
  return (Number(line.quantity) / 1000) * effectivePrice;
}

/** Format quantity number */
function fmtQty(val) {
  if (!val) return "0";
  return Number(val).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 1 });
}

/** Format currency amount — always 2 decimal places */
function fmtAmt(val) {
  const n = Number(val || 0);
  return n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
